import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, GitBranch, Users, MessageSquare, FileCode, Share2,
  Search, ChevronRight, ChevronDown, CheckCircle, AlertCircle, Loader2,
  Plus, Clock, XCircle, Info, RefreshCw, Check,
} from 'lucide-react';
import { formatRelativeTime, formatNumber } from '../lib/utils';
import { SEO } from '../components/SEO';
import { API_URL } from '../lib/config';
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('cp_token')}` };
}

interface Repo {
  _id: string;
  name: string;
  url: string;
  branch: string;
  provider: string;
  indexingStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  lastIndexedAt?: string;
  fileCount?: number;
}

type EntityType = 'repository' | 'service' | 'team' | 'adr' | 'pr' | 'doc';

interface KGEntity {
  id: string;
  type: EntityType;
  label: string;
  sub?: string;
  meta?: string;
  status?: string;
  url?: string;
}

const TYPE_CONFIG: Record<EntityType, {
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
  label: string;
  emptyTitle: string;
  emptyDesc: string;
}> = {
  repository: {
    icon: GitBranch,  color: '#64748B', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.22)', label: 'Repositories',
    emptyTitle: 'No repositories connected', emptyDesc: 'Connect a repository to start indexing your codebase.',
  },
  service: {
    icon: Share2,     color: '#3B82F6', bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.22)', label: 'Services',
    emptyTitle: 'No services detected', emptyDesc: 'Services are extracted automatically from your repositories after deep indexing.',
  },
  team: {
    icon: Users,      color: '#10B981', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.22)', label: 'Teams',
    emptyTitle: 'No teams found', emptyDesc: 'Team ownership is inferred from CODEOWNERS files and commit history.',
  },
  adr: {
    icon: FileCode,   color: '#F59E0B', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.22)', label: 'Decisions (ADRs)',
    emptyTitle: 'No ADRs detected', emptyDesc: 'Architecture Decision Records are extracted from docs and markdown files in your repositories.',
  },
  pr: {
    icon: GitBranch,  color: '#8B5CF6', bg: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.22)', label: 'Pull Requests',
    emptyTitle: 'No pull requests synced', emptyDesc: 'Pull request data is synced from your connected repositories.',
  },
  doc: {
    icon: BookOpen,   color: '#EC4899', bg: 'rgba(236,72,153,0.10)', border: 'rgba(236,72,153,0.22)', label: 'Documentation',
    emptyTitle: 'No documentation indexed', emptyDesc: 'Documentation is indexed from README and docs directories in your repositories.',
  },
};

function repoToEntity(repo: Repo): KGEntity {
  const statusMap: Record<string, string> = {
    completed:   'indexed',
    in_progress: 'indexing',
    pending:     'pending',
    failed:      'failed',
  };
  return {
    id: repo._id,
    type: 'repository',
    label: repo.name,
    sub: repo.fileCount != null ? `${formatNumber(repo.fileCount)} files` : `${repo.branch} branch`,
    meta: repo.lastIndexedAt ? `Indexed ${formatRelativeTime(repo.lastIndexedAt)}` : repo.provider,
    status: statusMap[repo.indexingStatus] ?? repo.indexingStatus,
    url: repo.url,
  };
}

/* ─── Status badge ──────────────────────────────────────────── */

function statusBadge(s: string | undefined): { style: React.CSSProperties; icon: React.ReactNode } | null {
  if (!s) return null;
  if (s === 'indexed' || s === 'merged' || s === 'accepted')
    return {
      style: { backgroundColor: 'var(--badge-success-bg)', color: 'var(--badge-success-color)', border: '1px solid var(--badge-success-border)' },
      icon: <CheckCircle className="w-2.5 h-2.5" />,
    };
  if (s === 'indexing' || s === 'pending')
    return {
      style: { backgroundColor: 'var(--badge-warn-bg)', color: 'var(--badge-warn-color)', border: '1px solid var(--badge-warn-border)' },
      icon: <Clock className="w-2.5 h-2.5" />,
    };
  if (s === 'failed')
    return {
      style: { backgroundColor: 'var(--badge-error-bg)', color: 'var(--badge-error-color)', border: '1px solid var(--badge-error-border)' },
      icon: <XCircle className="w-2.5 h-2.5" />,
    };
  if (s === 'open')
    return {
      style: { backgroundColor: 'var(--badge-info-bg)', color: 'var(--badge-info-color)', border: '1px solid var(--badge-info-border)' },
      icon: <AlertCircle className="w-2.5 h-2.5" />,
    };
  return null;
}

/* ─── Entity card ───────────────────────────────────────────── */

function EntityCard({ entity, selected, onClick }: {
  entity: KGEntity;
  selected: boolean;
  onClick: () => void;
}) {
  const tc = TYPE_CONFIG[entity.type];
  const Icon = tc.icon;
  const sb = statusBadge(entity.status);

  return (
    <div
      onClick={onClick}
      className="card p-3.5 cursor-pointer transition-all"
      style={selected ? {
        borderColor: 'var(--t1)',
        backgroundColor: 'var(--brand-dim)',
      } : {}}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = 'var(--b-strong)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = ''; }}
    >
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: tc.bg, border: `1px solid ${tc.border}` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: tc.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-t1 truncate leading-tight">{entity.label}</div>
          {entity.sub  && <div className="text-[10px] text-t3 mt-0.5">{entity.sub}</div>}
          {entity.meta && <div className="text-[10px] text-t4 mt-0.5">{entity.meta}</div>}
          {sb && (
            <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] px-2 py-0.5 rounded-full font-medium" style={sb.style}>
              {sb.icon}
              {entity.status}
            </span>
          )}
        </div>
        {selected && <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: tc.color }} />}
      </div>
    </div>
  );
}

/* ─── Empty type section ────────────────────────────────────── */

function EmptySection({ type }: { type: EntityType }) {
  const tc = TYPE_CONFIG[type];
  return (
    <div className="col-span-full rounded-xl p-8 text-center border"
      style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-3"
        style={{ backgroundColor: tc.bg, border: `1px solid ${tc.border}` }}>
        <tc.icon className="w-4.5 h-4.5" style={{ color: tc.color }} />
      </div>
      <p className="text-[13px] font-semibold text-t2 mb-1">{tc.emptyTitle}</p>
      <p className="text-[11px] text-t4 max-w-xs mx-auto leading-relaxed">{tc.emptyDesc}</p>
    </div>
  );
}

/* ─── Section header ────────────────────────────────────────── */

function SectionHeader({ type, count }: { type: EntityType; count: number }) {
  const tc = TYPE_CONFIG[type];
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-5 h-5 rounded flex items-center justify-center"
        style={{ backgroundColor: tc.bg, border: `1px solid ${tc.border}` }}>
        <tc.icon className="w-3 h-3" style={{ color: tc.color }} />
      </div>
      <span className="text-xs font-semibold text-t1">{tc.label}</span>
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: 'var(--s4)', color: 'var(--t3)' }}>
        {count}
      </span>
    </div>
  );
}

/* ─── Relationship panel ────────────────────────────────────── */

function RelationshipPanel({
  entity, archData, triggerReindex, reindexingId
}: {
  entity: KGEntity;
  archData?: ArchData;
  triggerReindex: (id: string) => void;
  reindexingId: string | null;
}) {
  const tc = TYPE_CONFIG[entity.type];
  const services = archData?.services ?? [];
  const adrs = archData?.adrs ?? [];
  const hasData = archData?.analyzed && (services.length > 0 || adrs.length > 0);
  const isReindexing = reindexingId === entity.id;
  const archLoading = entity.type === 'repository' && entity.status === 'indexed' && !archData;

  const SVC_COLORS: Record<string, string> = {
    frontend: '#38BDF8', api: '#60A5FA', worker: '#FB923C',
    library: '#A3A3A3', service: '#818CF8', app: '#818CF8',
  };

  const ADR_STATUS_CLS: Record<string, string> = {
    accepted: 'text-emerald-500', proposed: 'text-blue-500',
    superseded: 'text-amber-500', deprecated: 'text-red-500',
  };

  return (
    <div className="space-y-4">
      {/* Entity header */}
      <div className="flex items-center gap-2.5 p-3 rounded-xl border"
        style={{ borderColor: tc.border, backgroundColor: tc.bg }}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${tc.color}20` }}>
          <tc.icon className="w-3.5 h-3.5" style={{ color: tc.color }} />
        </div>
        <div className="min-w-0">
          <div className="text-[12px] font-semibold truncate" style={{ color: tc.color }}>{entity.label}</div>
          <div className="text-[10px] text-t3">{tc.label}</div>
        </div>
      </div>

      {archLoading && (
        <div className="flex items-center gap-2 text-xs text-t4">
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading architecture data…
        </div>
      )}

      {services.length > 0 && (
        <div>
          <div className="text-[10px] text-t3 uppercase tracking-widest font-semibold mb-2">
            Services ({services.length})
          </div>
          <div className="space-y-1.5">
            {services.map(svc => {
              const color = SVC_COLORS[svc.type] ?? '#818CF8';
              return (
                <div key={svc.id} className="flex items-center gap-2 p-2 rounded-lg border"
                  style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-t1 truncate">{svc.name}</div>
                    <div className="text-[9px] text-t4">{svc.lang}</div>
                  </div>
                  <span className="text-[9px] text-t4 capitalize flex-shrink-0">{svc.type}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {adrs.length > 0 && (
        <div>
          <div className="text-[10px] text-t3 uppercase tracking-widest font-semibold mb-2">
            Decisions · ADRs ({adrs.length})
          </div>
          <div className="space-y-1.5">
            {adrs.map(adr => (
              <div key={adr.id} className="p-2 rounded-lg border"
                style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
                <div className="text-[11px] text-t1 leading-tight">{adr.title}</div>
                <div className="flex items-center gap-2 mt-1">
                  {adr.status && (
                    <span className={`text-[9px] font-medium capitalize ${ADR_STATUS_CLS[adr.status] ?? 'text-t4'}`}>
                      {adr.status}
                    </span>
                  )}
                  {adr.date && <span className="text-[9px] text-t4">{adr.date}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!archLoading && !hasData && entity.type === 'repository' && archData !== undefined && (
        <div className="p-3.5 rounded-xl text-xs text-t3 leading-relaxed space-y-3 border"
          style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>No services or ADRs were extracted. Re-index this repository to extract architecture data automatically.</span>
          </div>
          <button
            onClick={() => triggerReindex(entity.id)}
            disabled={isReindexing}
            className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg w-full justify-center transition-colors disabled:opacity-50 hover:opacity-80"
            style={{ backgroundColor: 'var(--overlay-sm)', color: 'var(--t1)', border: '1px solid var(--b-default)' }}
          >
            {isReindexing
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Re-indexing…</>
              : <><RefreshCw className="w-3.5 h-3.5" /> Re-index Repository</>}
          </button>
        </div>
      )}

      <Link
        to={`/conversations?repo=${entity.id}&q=${encodeURIComponent(`Tell me about ${entity.label}`)}`}
        className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg border text-xs text-t2 hover:text-t1 transition-all"
        style={{ borderColor: 'var(--b-default)' }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--b-strong)';
          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--overlay-sm)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--b-default)';
          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '';
        }}
      >
        <MessageSquare className="w-3.5 h-3.5 text-t3 flex-shrink-0" />
        Ask AI about this
        <ChevronRight className="w-3 h-3 ml-auto text-t4" />
      </Link>

      {entity.url && (
        <a href={entity.url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border text-xs text-t3 hover:text-t1 transition-all truncate"
          style={{ borderColor: 'var(--b-subtle)' }}>
          <GitBranch className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{entity.url.replace('https://', '')}</span>
        </a>
      )}
    </div>
  );
}

interface ArchData {
  services: Array<{ id: string; name: string; type: string; lang: string; dirPath: string; framework?: string }>;
  edges: any[];
  adrs: Array<{ id: string; title: string; status?: string; date?: string; filePath?: string }>;
  analyzed: boolean;
}

/* ─── Main page ─────────────────────────────────────────────── */

export function KnowledgeGraphPage() {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [archCache, setArchCache] = useState<Record<string, ArchData>>({});
  const [typeFilter, setTypeFilter] = useState<EntityType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [reindexingId, setReindexingId] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRepos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/repositories`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setRepos(data.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRepos(); }, [loadRepos]);

  useEffect(() => {
    if (repos.length === 0) return;
    for (const repo of repos.filter(r => r.indexingStatus === 'completed')) {
      if (archCache[repo._id]) continue;
      fetch(`${API_URL}/repositories/${repo._id}/architecture`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => { if (d.success) setArchCache(prev => ({ ...prev, [repo._id]: d.data })); })
        .catch(() => {});
    }
  }, [repos]);

  useEffect(() => {
    if (!selected || archCache[selected]) return;
    fetch(`${API_URL}/repositories/${selected}/architecture`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.success) setArchCache(prev => ({ ...prev, [selected]: d.data })); })
      .catch(() => {});
  }, [selected]);

  const triggerReindex = useCallback(async (repoId: string) => {
    setReindexingId(repoId);
    try {
      await fetch(`${API_URL}/repositories/${repoId}/index`, { method: 'POST', headers: authHeaders() });
      const pollInterval = setInterval(async () => {
        const statusRes = await fetch(`${API_URL}/repositories/${repoId}/status`, { headers: authHeaders() });
        const statusData = await statusRes.json();
        if (statusData.success && statusData.data.indexingStatus === 'completed') {
          clearInterval(pollInterval);
          setReindexingId(null);
          const archRes = await fetch(`${API_URL}/repositories/${repoId}/architecture`, { headers: authHeaders() });
          const archJson = await archRes.json();
          if (archJson.success) setArchCache(prev => ({ ...prev, [repoId]: archJson.data }));
          loadRepos();
        } else if (statusData.success && statusData.data.indexingStatus === 'failed') {
          clearInterval(pollInterval);
          setReindexingId(null);
        }
      }, 3000);
    } catch { setReindexingId(null); }
  }, [loadRepos]);

  const repoEntities: KGEntity[] = repos.map(repoToEntity);

  const filteredRepos = repoEntities.filter(e => {
    if (typeFilter !== 'all' && typeFilter !== 'repository') return false;
    if (search && !e.label.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selectedEntity = repoEntities.find(e => e.id === selected) ?? null;
  const NON_REPO_TYPES: EntityType[] = ['service', 'team', 'adr', 'pr', 'doc'];
  const showNonRepo = typeFilter === 'all' && !search;

  const TYPE_FILTERS: Array<{ key: EntityType | 'all'; label: string }> = [
    { key: 'all',        label: 'All' },
    { key: 'repository', label: 'Repositories' },
    { key: 'service',    label: 'Services' },
    { key: 'team',       label: 'Teams' },
    { key: 'adr',        label: 'Decisions (ADRs)' },
    { key: 'pr',         label: 'Pull Requests' },
  ];

  return (
    <div className="flex h-full">
      <SEO title="Knowledge Graph" description="Explore your codebase as a knowledge graph. Browse repositories, services, teams, and architecture decisions extracted automatically from your code." noIndex />

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Toolbar */}
        <div className="h-11 flex-shrink-0 border-b px-4 flex items-center gap-3"
          style={{ borderColor: 'var(--b-subtle)' }}>
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-t3 flex-shrink-0" />
            <span className="text-sm font-semibold text-t1">Knowledge Graph</span>
            {!loading && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'var(--s4)', color: 'var(--t3)' }}>
                {repos.length} {repos.length === 1 ? 'repository' : 'repositories'}
              </span>
            )}
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border ml-4 transition-colors"
            style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-default)', minWidth: '200px' }}
            onFocusCapture={e => (e.currentTarget.style.borderColor = 'var(--b-strong)')}
            onBlurCapture={e => (e.currentTarget.style.borderColor = 'var(--b-default)')}
          >
            <Search className="w-3.5 h-3.5 text-t4 flex-shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search repositories…"
              className="flex-1 bg-transparent text-xs text-t1 placeholder-t4 outline-none"
            />
          </div>

          {/* Type filter dropdown */}
          <div ref={filterRef} className="relative ml-auto flex-shrink-0">
            <button
              onClick={() => setFilterOpen(o => !o)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: 'var(--s2)',
                border: '1px solid var(--b-default)',
                color: 'var(--t2)',
                minWidth: '140px',
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--b-strong)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = filterOpen ? 'var(--b-strong)' : 'var(--b-default)')}
            >
              {typeFilter !== 'all' && (
                <span className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: TYPE_CONFIG[typeFilter].color }} />
              )}
              <span className="flex-1 text-left truncate">
                {typeFilter === 'all' ? 'All types' : TYPE_CONFIG[typeFilter].label}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-t4 flex-shrink-0 transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
            </button>

            {filterOpen && (
              <div
                className="absolute right-0 top-full mt-1.5 rounded-xl overflow-hidden z-20"
                style={{
                  backgroundColor: 'var(--s2)',
                  border: '1px solid var(--b-default)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
                  minWidth: '180px',
                }}
              >
                <div className="py-1">
                  {TYPE_FILTERS.map(({ key, label }) => {
                    const isActive = typeFilter === key;
                    const tc = key !== 'all' ? TYPE_CONFIG[key] : null;
                    return (
                      <button
                        key={key}
                        onClick={() => { setTypeFilter(key); setFilterOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors text-left"
                        style={isActive
                          ? { backgroundColor: 'var(--brand-dim)', color: 'var(--t1)' }
                          : { color: 'var(--t2)' }
                        }
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--overlay-sm)'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {tc ? (
                          <span className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: tc.bg, border: `1px solid ${tc.border}` }}>
                            <tc.icon className="w-3 h-3" style={{ color: tc.color }} />
                          </span>
                        ) : (
                          <span className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: 'var(--s4)' }}>
                            <BookOpen className="w-3 h-3 text-t3" />
                          </span>
                        )}
                        <span className="flex-1 font-medium">{label}</span>
                        {isActive && <Check className="w-3 h-3 text-t1 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Entity grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-5 h-5 animate-spin text-t4" />
            </div>
          ) : repos.length === 0 && !search ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-14 h-14 rounded-2xl border flex items-center justify-center mb-5"
                style={{ backgroundColor: 'var(--s2)', borderColor: 'var(--b-subtle)' }}>
                <BookOpen className="w-7 h-7 text-t4" />
              </div>
              <h3 className="text-base font-semibold text-t1 mb-2">Knowledge graph is empty</h3>
              <p className="text-sm text-t3 max-w-xs leading-relaxed mb-6">
                Connect and index repositories to populate services, teams, ADRs, and more.
              </p>
              <Link to="/repositories" className="btn-primary">
                <Plus className="w-4 h-4" />
                Connect Repository
              </Link>
            </div>
          ) : (
            <div className="space-y-8">

              {/* Repositories section */}
              {(typeFilter === 'all' || typeFilter === 'repository') && (
                <div>
                  <SectionHeader type="repository" count={filteredRepos.length} />
                  {filteredRepos.length === 0 ? (
                    <p className="text-xs text-t4 px-1">No repositories match your search.</p>
                  ) : (
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-2.5">
                      {filteredRepos.map(entity => (
                        <EntityCard
                          key={entity.id}
                          entity={entity}
                          selected={selected === entity.id}
                          onClick={() => setSelected(selected === entity.id ? null : entity.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Non-repo sections when "All" is selected */}
              {showNonRepo && NON_REPO_TYPES.map(type => (
                <div key={type}>
                  <SectionHeader type={type} count={0} />
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-2.5">
                    <EmptySection type={type} />
                  </div>
                </div>
              ))}

              {/* Focused empty state when a non-repo type filter is selected */}
              {typeFilter !== 'all' && typeFilter !== 'repository' && (
                <div>
                  <SectionHeader type={typeFilter} count={0} />
                  <div className="grid grid-cols-1 gap-2.5 max-w-lg">
                    <EmptySection type={typeFilter} />
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {/* Right panel */}
      <div className="w-[280px] flex-shrink-0 border-l flex flex-col"
        style={{ borderColor: 'var(--b-subtle)', backgroundColor: 'var(--s0)' }}>
        {selectedEntity ? (
          <>
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--b-subtle)' }}>
              <span className="text-xs font-semibold text-t1">Details</span>
              <button
                onClick={() => setSelected(null)}
                className="text-[10px] text-t4 hover:text-t2 transition-colors px-2 py-0.5 rounded"
                style={{ backgroundColor: 'var(--overlay-sm)' }}
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <RelationshipPanel
                entity={selectedEntity}
                archData={selected ? archCache[selected] : undefined}
                triggerReindex={triggerReindex}
                reindexingId={reindexingId}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-10 h-10 rounded-xl border flex items-center justify-center mb-4"
              style={{ backgroundColor: 'var(--s2)', borderColor: 'var(--b-default)' }}>
              <BookOpen className="w-5 h-5 text-t4" />
            </div>
            <p className="text-sm font-medium text-t2 mb-1">Select an entity</p>
            <p className="text-xs text-t4 leading-relaxed">
              Click any card to explore its details and relationships.
            </p>
          </div>
        )}

        {/* Summary stats */}
        <div className="border-t p-4" style={{ borderColor: 'var(--b-subtle)' }}>
          <div className="text-[10px] font-semibold text-t4 uppercase tracking-widest mb-3">Graph Summary</div>
          <div className="space-y-2">
            {[
              { label: 'Repositories', color: TYPE_CONFIG.repository.color, value: repos.length },
              { label: 'Indexed', color: '#10B981', value: repos.filter(r => r.indexingStatus === 'completed').length },
            ].map(row => (
              <div key={row.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: row.color }} />
                  <span className="text-[11px] text-t3">{row.label}</span>
                </div>
                <span className="text-[11px] font-mono font-semibold text-t2">{row.value}</span>
              </div>
            ))}
            {(() => {
              const allArch = Object.values(archCache);
              const totalServices = allArch.reduce((s, a) => s + (a.services?.length ?? 0), 0);
              const totalAdrs = allArch.reduce((s, a) => s + (a.adrs?.length ?? 0), 0);
              return (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: TYPE_CONFIG.service.color }} />
                      <span className="text-[11px] text-t3">Services</span>
                    </div>
                    <span className="text-[11px] font-mono font-semibold text-t2">{totalServices > 0 ? totalServices : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: TYPE_CONFIG.adr.color }} />
                      <span className="text-[11px] text-t3">Decisions (ADRs)</span>
                    </div>
                    <span className="text-[11px] font-mono font-semibold text-t2">{totalAdrs > 0 ? totalAdrs : '—'}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
